import React, { useMemo } from 'react';
import { Check, FileClock, House, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type { PayrollVisitItem } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { getVisitTypeDisplayName } from '../../hooks/useVisitTypeOptions';
import { VISIT_TYPE_LABELS } from '../../constants/visitTypeLabels';
import { employeeTypeLabel } from '../../constants/employeeTypes';
import { ReportLink } from '../reporting/ReportLink';
import { patientChartPath, patientVisitPath } from '../../utils/navigation';
import { payrollStatusLabel } from './payrollStatusLabels';

type PayrollGroupBy = 'clinician' | 'visit_date';

type PayrollVisitsTableProps = {
  visits: PayrollVisitItem[];
  /** Manager groups by clinician; the payroll-detail screen by visit date. */
  groupBy: PayrollGroupBy;
  /** Extra Clinic column (payroll-detail screen). */
  showClinicColumn?: boolean;
  /** Per-row Clinician column (payroll-detail screen, where rows group by date). */
  showClinicianColumn?: boolean;
  /** Visit status column (Payroll Manager). */
  showStatusColumn?: boolean;
  /** Selection controls (Payroll Manager only). */
  selectedIds?: Set<string>;
  onToggleSelect?: (visitId: string) => void;
  onToggleSelectAll?: () => void;
  /** Per-row remove action (payroll-detail screen). */
  onRemoveVisit?: (visit: PayrollVisitItem) => void;
};

/** Patient, Employee Type, Date of Visit, Visit Name, Visit Rate, Mileage,
 *  Surcharge, Insurance Provider — the columns every payroll table renders.
 *  Keep in sync with the header/body below so the group-header colSpan stays
 *  correct. The signature marker rides inside the Patient cell. */
const ALWAYS_ON_COLUMN_COUNT = 8;

type VisitGroup = {
  key: string;
  label: string;
  visits: PayrollVisitItem[];
};

function formatMoney(value: string | number): string {
  const amount = Number(value);
  return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
}

function employeeTitlesLabel(titles: string[] | null | undefined): string {
  return (titles ?? [])
    .map(title => title.trim())
    .filter(title => title.length > 0)
    .join(', ');
}

function payrollEmployeeTypeLabel(visit: PayrollVisitItem): string {
  const label = employeeTypeLabel(
    visit.employee_type,
    visit.employee_type_other
  );
  return label.trim() || 'Unassigned';
}

/** Group consecutive rows; server ordering keeps groups contiguous. */
function buildGroups(
  visits: PayrollVisitItem[],
  groupBy: PayrollGroupBy
): VisitGroup[] {
  const groups: VisitGroup[] = [];
  for (const visit of visits) {
    const key =
      groupBy === 'clinician'
        ? (visit.clinician_user_id ?? 'unassigned')
        : visit.visit_date;
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.visits.push(visit);
      continue;
    }
    const label =
      groupBy === 'clinician'
        ? (visit.clinician_name ?? 'Unassigned')
        : formatDateToMMDDYYYY(visit.visit_date);
    groups.push({ key, label, visits: [visit] });
  }
  return groups;
}

/** Badge colour and tooltip copy per signature state. */
const SIGNATURE_MARKERS = {
  signed: {
    label: 'Patient signed',
    badge: 'bg-emerald-600',
    glyph: <Check strokeWidth={4} className='h-1.5 w-1.5 text-white' />,
    detail: 'The patient signed for this visit.',
  },
  unable_to_sign: {
    label: 'Patient unable to sign',
    badge: 'bg-amber-500',
    glyph: (
      <span className='text-[7px] font-bold leading-none text-white'>!</span>
    ),
    // Worth spelling out: this is not a missing signature, and the reason it
    // is missing was recorded. Payroll's next question is always "why", and
    // the answer is one place away.
    detail:
      'The clinician recorded that the patient was unable to sign. The reason is documented in the visit note.',
  },
  // Roger holds no signature for this visit, and that is not a gap:
  // it was documented in the previous EMR before import, and the signature
  // record stays there. No house and no badge, so it cannot read as signed.
  previous_emr: {
    label: 'Signature stays in External EMR',
    badge: null,
    glyph: null,
    detail:
      'This visit was documented in External EMR before import. Its patient signature stays there.',
  },
} as const;

/**
 * The signature marker beside a patient's name.
 *
 * Both states are the same house with a different corner badge, so they read
 * as one object with two statuses rather than two unrelated glyphs. The check
 * and the "!" sit on a solid disc rather than loose over the house: a bare
 * stroke landed on the roof lines with nothing to punch through, and at this
 * size the two merged. An opaque disc also survives the row's hover tint,
 * which a background-coloured ring would not.
 *
 * `missing` renders nothing — absence is the third state, and a marker for it
 * would triple the visual noise on the rows nobody needs to look at.
 * `previous_emr` gets the dated-file mark the other surfaces use: it is not a
 * signature, so it must not be a house with a badge.
 */
const PatientSignatureMarker: React.FC<{
  state: PayrollVisitItem['patient_signature'];
}> = ({ state }) => {
  if (
    state !== 'signed' &&
    state !== 'unable_to_sign' &&
    state !== 'previous_emr'
  ) {
    return null;
  }
  const marker = SIGNATURE_MARKERS[state];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* tabIndex so the tooltip is reachable without a mouse; role='img'
            plus aria-label because an aria-label on a bare lucide <svg> gives
            no reliable accessible name. */}
        <span
          tabIndex={0}
          className='relative inline-flex shrink-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
        >
          {marker.badge === null ? (
            <FileClock
              role='img'
              aria-label={marker.label}
              className='h-4 w-4 text-muted-foreground'
            />
          ) : (
            <>
              <House
                role='img'
                aria-label={marker.label}
                className='h-4 w-4 text-muted-foreground'
              />
              <span
                aria-hidden='true'
                className={`absolute -bottom-0.5 -right-0.5 inline-flex h-2.5 w-2.5 items-center justify-center rounded-full ${marker.badge}`}
              >
                {marker.glyph}
              </span>
            </>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side='top' className='max-w-xs text-xs'>
        {marker.detail}
      </TooltipContent>
    </Tooltip>
  );
};

export const PayrollVisitsTable: React.FC<PayrollVisitsTableProps> = ({
  visits,
  groupBy,
  showClinicColumn = false,
  showClinicianColumn = false,
  showStatusColumn = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onRemoveVisit,
}) => {
  const selectable = !!selectedIds && !!onToggleSelect;
  const groups = useMemo(() => buildGroups(visits, groupBy), [visits, groupBy]);
  const columnCount =
    ALWAYS_ON_COLUMN_COUNT +
    (selectable ? 1 : 0) +
    (showClinicColumn ? 1 : 0) +
    (showClinicianColumn ? 1 : 0) +
    (showStatusColumn ? 1 : 0) +
    (onRemoveVisit ? 1 : 0);

  return (
    // The detail screen reaches 11 columns. A min width makes the table scroll
    // inside the Table's own overflow-auto container on a phone, rather than
    // crushing every column into tall wrapped rows (AGENTS.md, mobile layout).
    <TooltipProvider delayDuration={300}>
      <Table className='min-w-[1000px]'>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className='w-10'>
                <Checkbox
                  checked={
                    visits.length > 0 && selectedIds.size === visits.length
                  }
                  onCheckedChange={onToggleSelectAll}
                />
              </TableHead>
            )}
            <TableHead>Patient</TableHead>
            <TableHead>Employee Type</TableHead>
            {showClinicianColumn && <TableHead>Clinician</TableHead>}
            <TableHead>Date of Visit</TableHead>
            <TableHead>Visit Name</TableHead>
            <TableHead className='text-right'>Visit Rate</TableHead>
            <TableHead className='text-right'>Mileage</TableHead>
            <TableHead className='text-right'>Surcharge</TableHead>
            {showStatusColumn && <TableHead>Status</TableHead>}
            {showClinicColumn && <TableHead>Clinic</TableHead>}
            <TableHead>Insurance Provider</TableHead>
            {onRemoveVisit && (
              <TableHead className='w-20 text-center'>Remove</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(group => (
            <React.Fragment key={group.key}>
              <TableRow className='bg-muted/60 hover:bg-muted/60'>
                <TableCell
                  colSpan={columnCount}
                  className='py-1.5 text-sm font-semibold'
                >
                  {group.label}
                  {groupBy === 'clinician' && group.visits[0] && (
                    <>
                      {employeeTitlesLabel(group.visits[0].employee_titles) ? (
                        <span className='ml-2 font-normal text-muted-foreground'>
                          (
                          {employeeTitlesLabel(group.visits[0].employee_titles)}
                          )
                        </span>
                      ) : null}
                      {employeeTypeLabel(
                        group.visits[0].employee_type,
                        group.visits[0].employee_type_other
                      ) && (
                        <span className='ml-2 font-normal text-muted-foreground'>
                          {employeeTypeLabel(
                            group.visits[0].employee_type,
                            group.visits[0].employee_type_other
                          )}
                        </span>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
              {group.visits.map(visit => (
                <TableRow key={visit.visit_id}>
                  {selectable && (
                    <TableCell className='w-10'>
                      <Checkbox
                        checked={selectedIds.has(visit.visit_id)}
                        onCheckedChange={() => onToggleSelect(visit.visit_id)}
                      />
                    </TableCell>
                  )}
                  <TableCell className='text-sm font-medium'>
                    <span className='inline-flex items-center gap-1.5'>
                      <ReportLink to={patientChartPath(visit.patient_id)}>
                        {visit.patient_name}
                      </ReportLink>
                      <PatientSignatureMarker state={visit.patient_signature} />
                    </span>
                  </TableCell>
                  <TableCell className='text-sm'>
                    {payrollEmployeeTypeLabel(visit)}
                  </TableCell>
                  {showClinicianColumn && (
                    <TableCell className='text-sm'>
                      <div>{visit.clinician_name ?? 'Unassigned'}</div>
                      {employeeTitlesLabel(visit.employee_titles) && (
                        <div className='text-xs text-muted-foreground'>
                          ({employeeTitlesLabel(visit.employee_titles)})
                        </div>
                      )}
                    </TableCell>
                  )}
                  <TableCell className='text-sm'>
                    {formatDateToMMDDYYYY(visit.visit_date)}
                  </TableCell>
                  <TableCell className='text-sm'>
                    <ReportLink
                      to={patientVisitPath(visit.patient_id, visit.visit_id)}
                    >
                      {VISIT_TYPE_LABELS[visit.visit_type] ??
                        getVisitTypeDisplayName(visit.visit_type)}
                      {visit.alias_name && ` (${visit.alias_name})`}
                    </ReportLink>
                  </TableCell>
                  <TableCell className='text-right text-sm'>
                    {formatMoney(visit.visit_rate ?? 0)}
                  </TableCell>
                  <TableCell className='text-right text-sm'>
                    {visit.mileage ?? 0}
                  </TableCell>
                  <TableCell className='text-right text-sm'>
                    {formatMoney(visit.surcharge ?? 0)}
                  </TableCell>
                  {showStatusColumn && (
                    <TableCell className='text-sm'>
                      {payrollStatusLabel(visit.status)}
                    </TableCell>
                  )}
                  {showClinicColumn && (
                    <TableCell className='text-sm'>
                      {visit.clinic_name ?? '—'}
                    </TableCell>
                  )}
                  <TableCell className='text-sm'>
                    {visit.insurance_name ?? '—'}
                  </TableCell>
                  {onRemoveVisit && (
                    <TableCell className='w-20 text-center'>
                      <Button
                        size='sm'
                        variant='ghost'
                        aria-label='Remove visit from payroll'
                        onClick={() => onRemoveVisit(visit)}
                      >
                        <Trash2 className='h-4 w-4 text-destructive' />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};
